import React, { useState, useEffect } from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { Eye, Clock, Calendar, CheckCircle2 } from 'lucide-react';
import { apiClient } from '../lib/api/client';
import { formatRelativeTime } from '../lib/utils/time';
import { PageLayout } from '@/layouts';
import { VStack, HStack } from '@/components/core';

interface Visitor {
  id: string;
  name: string;
  avatar_url: string;
}

interface ProfileVisit {
  id: string;
  visitor: Visitor;
  visited_at: string;
  duration_seconds: number;
  source: string;
  is_seen: boolean;
}

interface ProfileVisitsResponse {
  visits: ProfileVisit[];
  total_unseen: number;
}

type TimeFilter = 'today' | 'week' | 'all';

export default function ProfileVisits() {
  const [visits, setVisits] = useState<ProfileVisit[]>([]);
  const [totalUnseen, setTotalUnseen] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timeFilter, setTimeFilter] = useState<TimeFilter>('all');
  const [selectedVisits, setSelectedVisits] = useState<Set<string>>(new Set());
  const [markingAsSeen, setMarkingAsSeen] = useState(false);

  useEffect(() => {
    void fetchVisits();
  }, []);

  const fetchVisits = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get<ProfileVisitsResponse>('/profile-visits');
      setVisits(response.data.visits);
      setTotalUnseen(response.data.total_unseen);
    } catch (err: unknown) {
      setError('Fehler beim Laden der Profilbesuche');
      console.error('Error fetching profile visits:', err);
    } finally {
      setLoading(false);
    }
  };

  const markAsSeen = async () => {
    if (selectedVisits.size === 0) return;

    try {
      setMarkingAsSeen(true);
      await apiClient.post('/profile-visits/mark-seen', {
        visit_ids: Array.from(selectedVisits)
      });
      
      // Update local state
      setVisits(visits.map(visit => 
        selectedVisits.has(visit.id) 
          ? { ...visit, is_seen: true }
          : visit
      ));
      setTotalUnseen(Math.max(0, totalUnseen - selectedVisits.size));
      setSelectedVisits(new Set());
    } catch (err: unknown) {
      console.error('Error marking visits as seen:', err);
    } finally {
      setMarkingAsSeen(false);
    }
  };

  const formatDuration = (seconds: number): string => {
    if (seconds < 60) return `${seconds} Sekunden`;
    if (seconds < 3600) {
      const minutes = Math.floor(seconds / 60);
      return `${minutes} Minute${minutes !== 1 ? 'n' : ''}`;
    }
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours} Stunde${hours !== 1 ? 'n' : ''} ${minutes} Min`;
  };

  const filterVisits = (visits: ProfileVisit[]): ProfileVisit[] => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

    switch (timeFilter) {
      case 'today':
        return visits.filter(visit => new Date(visit.visited_at) >= today);
      case 'week':
        return visits.filter(visit => new Date(visit.visited_at) >= weekAgo);
      case 'all':
      default:
        return visits;
    }
  };

  const toggleVisitSelection = (visitId: string) => {
    const newSelection = new Set(selectedVisits);
    if (newSelection.has(visitId)) {
      newSelection.delete(visitId);
    } else {
      newSelection.add(visitId);
    }
    setSelectedVisits(newSelection);
  };

  const selectAllUnseen = () => {
    const unseenIds = visits
      .filter(visit => !visit.is_seen)
      .map(visit => visit.id);
    setSelectedVisits(new Set(unseenIds));
  };

  const filteredVisits = filterVisits(visits);

  if (loading) {
    return (
      <PageLayout maxWidth="xl" padding="md">
        <div className="bg-muted rounded-token-button shadow-token-modal">
          {/* Header Skeleton */}
          <VStack gap="md" className="p-token-lg border-b border-muted-foreground/20">
            <HStack justify="between" fullWidth>
              <HStack gap="sm" align="center">
                <Skeleton className="w-6 h-6 rounded bg-white/10" />
                <Skeleton className="h-8 w-48 bg-white/10" />
              </HStack>
              <Skeleton className="h-6 w-16 rounded-full bg-white/10" />
            </HStack>

            {/* Filter Buttons Skeleton */}
            <HStack gap="md" justify="between" wrap fullWidth className="flex-col sm:flex-row items-start sm:items-center">
              <HStack gap="sm">
                <Skeleton className="h-10 w-20 rounded-token-button bg-white/10" />
                <Skeleton className="h-10 w-28 rounded-token-button bg-white/10" />
                <Skeleton className="h-10 w-16 rounded-token-button bg-white/10" />
              </HStack>
              <Skeleton className="h-10 w-36 rounded-token-button bg-white/10" />
            </HStack>
          </VStack>

          {/* Visit Items Skeleton */}
          <VStack gap="none" className="divide-y divide-muted-foreground/20">
            {Array.from({ length: 6 }).map((_, index) => (
              <div key={index} className="p-token-md">
                <HStack gap="md" align="center">
                  <div className="relative">
                    <Skeleton className="w-4 h-4 rounded absolute top-0 left-0 bg-white/10" />
                    <Skeleton className="w-16 h-16 rounded-full ml-token-lg bg-white/10" />
                  </div>

                  <VStack gap="xs" className="flex-1">
                    <HStack justify="between" fullWidth align="start">
                      <VStack gap="xs">
                        <Skeleton className="h-5 w-32 bg-white/10" />
                        <HStack gap="md">
                          <HStack gap="xs" align="center">
                            <Skeleton className="w-4 h-4 bg-white/10" />
                            <Skeleton className="h-4 w-20 bg-white/10" />
                          </HStack>
                          <HStack gap="xs" align="center">
                            <Skeleton className="w-4 h-4 bg-white/10" />
                            <Skeleton className="h-4 w-24 bg-white/10" />
                          </HStack>
                        </HStack>
                      </VStack>
                      <Skeleton className="h-6 w-16 rounded bg-white/10" />
                    </HStack>
                  </VStack>
                </HStack>
              </div>
            ))}
          </VStack>
        </div>
      </PageLayout>
    );
  }

  if (error) {
    return (
      <PageLayout maxWidth="xl" padding="md">
        <VStack gap="md" align="center" justify="center" className="min-h-[400px]">
          <p className="text-error">{error}</p>
          <button
            onClick={() => { void fetchVisits(); }}
            className="px-token-md py-token-sm bg-brand-primary text-white rounded-token-button hover:bg-brand-primary-dark transition-colors"
          >
            Erneut versuchen
          </button>
        </VStack>
      </PageLayout>
    );
  }

  return (
    <PageLayout maxWidth="xl" padding="md">
      <div className="bg-muted rounded-token-button shadow-token-modal">
        <VStack gap="md" className="p-token-lg border-b border-muted-foreground/20">
          <HStack justify="between" fullWidth>
            <HStack gap="sm" align="center">
              <Eye className="w-6 h-6 text-white" />
              <h1 className="text-2xl font-bold text-white">Profilbesuche</h1>
            </HStack>
            {totalUnseen > 0 && (
              <span className="bg-brand-primary text-white text-sm px-token-sm py-token-xs rounded-full">
                {totalUnseen} neu
              </span>
            )}
          </HStack>

          <HStack gap="md" justify="between" wrap fullWidth className="flex-col sm:flex-row items-start sm:items-center">
            <HStack gap="sm">
              <button
                onClick={() => setTimeFilter('today')}
                className={`px-token-md py-token-sm rounded-token-button transition-colors ${
                  timeFilter === 'today'
                    ? 'bg-brand-primary text-white'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                Heute
              </button>
              <button
                onClick={() => setTimeFilter('week')}
                className={`px-token-md py-token-sm rounded-token-button transition-colors ${
                  timeFilter === 'week'
                    ? 'bg-brand-primary text-white'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                Diese Woche
              </button>
              <button
                onClick={() => setTimeFilter('all')}
                className={`px-token-md py-token-sm rounded-token-button transition-colors ${
                  timeFilter === 'all'
                    ? 'bg-brand-primary text-white'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                Alle
              </button>
            </HStack>

            {selectedVisits.size > 0 && (
              <button
                onClick={() => { void markAsSeen(); }}
                disabled={markingAsSeen}
                className="px-token-md py-token-sm bg-success text-white rounded-token-button hover:bg-success/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-token-sm"
              >
                <CheckCircle2 className="w-4 h-4" />
                Als gesehen markieren ({selectedVisits.size})
              </button>
            )}

            {totalUnseen > 0 && selectedVisits.size === 0 && (
              <button
                onClick={selectAllUnseen}
                className="px-token-md py-token-sm bg-muted text-muted-foreground rounded-token-button hover:bg-muted/80 transition-colors text-sm"
              >
                Alle neuen auswählen
              </button>
            )}
          </HStack>
        </VStack>

        <VStack gap="none" className="divide-y divide-muted-foreground/20">
          {filteredVisits.length === 0 ? (
            <VStack gap="md" align="center" className="p-token-xl text-muted-foreground">
              <Eye className="w-12 h-12 opacity-50" />
              <p>Keine Profilbesuche fuer diesen Zeitraum</p>
            </VStack>
          ) : (
            filteredVisits.map((visit) => (
              <div
                key={visit.id}
                className={`p-token-md hover:bg-muted/50 transition-colors cursor-pointer ${
                  !visit.is_seen ? 'bg-muted/30' : ''
                }`}
                onClick={() => toggleVisitSelection(visit.id)}
              >
                <HStack gap="md" align="center">
                  <div className="relative">
                    <Checkbox
                      checked={selectedVisits.has(visit.id)}
                      onCheckedChange={() => toggleVisitSelection(visit.id)}
                      onClick={(e) => { e.stopPropagation(); }}
                      className="absolute top-0 left-0"
                    />
                    <img
                      src={visit.visitor.avatar_url || '/default-avatar.png'}
                      alt={visit.visitor.name}
                      className="w-16 h-16 rounded-full object-cover ml-token-lg"
                    />
                    {!visit.is_seen && (
                      <div className="absolute -top-1 -right-1 w-3 h-3 bg-brand-primary rounded-full"></div>
                    )}
                  </div>

                  <VStack gap="xs" className="flex-1">
                    <HStack justify="between" fullWidth align="start">
                      <VStack gap="xs">
                        <h3 className="font-semibold text-white">
                          {visit.visitor.name}
                        </h3>
                        <HStack gap="md" className="text-sm text-muted-foreground">
                          <HStack gap="xs" align="center">
                            <Calendar className="w-4 h-4" />
                            <span>{formatRelativeTime(new Date(visit.visited_at))}</span>
                          </HStack>
                          <HStack gap="xs" align="center">
                            <Clock className="w-4 h-4" />
                            <span>{formatDuration(visit.duration_seconds)}</span>
                          </HStack>
                        </HStack>
                      </VStack>
                      {visit.source && (
                        <span className="text-xs text-muted-foreground bg-muted px-token-sm py-token-xs rounded">
                          {visit.source}
                        </span>
                      )}
                    </HStack>
                  </VStack>
                </HStack>
              </div>
            ))
          )}
        </VStack>
      </div>
    </PageLayout>
  );
}